/**
 * Activity-weighted rollups for the accounting view. Single home so
 * the math stays consistent across the four reports.
 *
 * Per the spec §"Rollup math (important — easy to get wrong)":
 * monthly / annual rollups must sum the underlying activity counts and
 * dollars, NEVER average the weekly percentages. Worked example from
 * the spec:
 *   Week 1: 10 commits, 8 Growth → 80% Growth
 *   Week 2: 50 commits, 10 Growth → 20% Growth
 *   Month: 18/60 = 30% Growth (NOT (80+20)/2 = 50%)
 * The rollups below sum dollars and counts, never percentages. The
 * percentage is derived once at render time from the summed totals.
 */

import type {
  AccountingTreatment,
  AccountingView,
  ClassifiedCommit,
  InvestmentType,
  OpExSubtype,
} from './types';

// ---- Building blocks ----------------------------------------------------

export interface CellTotals {
  count: number;
  cost_usd: number;
}

export function emptyTotals(): CellTotals {
  return { count: 0, cost_usd: 0 };
}

export function addTotals(a: CellTotals, b: CellTotals): CellTotals {
  return { count: a.count + b.count, cost_usd: a.cost_usd + b.cost_usd };
}

function bumpCell<K extends string>(
  acc: Record<K, CellTotals>,
  key: K,
  c: ClassifiedCommit,
): void {
  const cur = acc[key] ?? emptyTotals();
  acc[key] = { count: cur.count + 1, cost_usd: cur.cost_usd + c.cost_usd };
}

// ---- Report 1: Investment × BusinessFunction ----------------------------

export interface InvestmentByFunctionRollup {
  /** key: `${InvestmentType}|${business_function_id}` */
  cells: Record<string, CellTotals>;
  /** Per-row totals across all BFs. */
  investmentTotals: Record<InvestmentType, CellTotals>;
  /** Per-column totals across all investment types. */
  functionTotals: Record<string, CellTotals>;
  grandTotal: CellTotals;
}

export function rollupInvestmentByFunction(
  view: AccountingView,
): InvestmentByFunctionRollup {
  const cells: Record<string, CellTotals> = {};
  const investmentTotals: Record<InvestmentType, CellTotals> = {
    ktlo: emptyTotals(),
    enablement: emptyTotals(),
    growth: emptyTotals(),
  };
  const functionTotals: Record<string, CellTotals> = {};
  let grandTotal: CellTotals = emptyTotals();

  for (const c of view.commits) {
    const inv = c.decision.investment_type;
    const bf = c.business_function_id;
    bumpCell(cells, `${inv}|${bf}`, c);
    investmentTotals[inv] = addTotals(investmentTotals[inv], { count: 1, cost_usd: c.cost_usd });
    functionTotals[bf] = addTotals(functionTotals[bf] ?? emptyTotals(), { count: 1, cost_usd: c.cost_usd });
    grandTotal = addTotals(grandTotal, { count: 1, cost_usd: c.cost_usd });
  }

  return { cells, investmentTotals, functionTotals, grandTotal };
}

// ---- Report 2: Treatment × BusinessFunction -----------------------------

export type TreatmentRow = 'opex_rd' | 'opex_other' | 'capex';

export interface TreatmentByFunctionRollup {
  /** key: `${TreatmentRow}|${business_function_id}` */
  cells: Record<string, CellTotals>;
  treatmentTotals: Record<TreatmentRow, CellTotals>;
  functionTotals: Record<string, CellTotals>;
  grandTotal: CellTotals;
  /** Activities flagged for review (overlap with the totals above — shown as a sub-row). */
  flagged: {
    cells: Record<string, CellTotals>;
    total: CellTotals;
  };
}

export function treatmentRowFor(
  treatment: AccountingTreatment,
  opex_subtype: OpExSubtype | null,
): TreatmentRow {
  if (treatment === 'capex') return 'capex';
  if (opex_subtype === 'rd') return 'opex_rd';
  return 'opex_other';
}

export function rollupTreatmentByFunction(
  view: AccountingView,
): TreatmentByFunctionRollup {
  const cells: Record<string, CellTotals> = {};
  const treatmentTotals: Record<TreatmentRow, CellTotals> = {
    opex_rd: emptyTotals(),
    opex_other: emptyTotals(),
    capex: emptyTotals(),
  };
  const functionTotals: Record<string, CellTotals> = {};
  let grandTotal: CellTotals = emptyTotals();
  const flaggedCells: Record<string, CellTotals> = {};
  let flaggedTotal: CellTotals = emptyTotals();

  for (const c of view.commits) {
    const row = treatmentRowFor(c.decision.accounting_treatment, c.decision.opex_subtype);
    const bf = c.business_function_id;
    bumpCell(cells, `${row}|${bf}`, c);
    treatmentTotals[row] = addTotals(treatmentTotals[row], { count: 1, cost_usd: c.cost_usd });
    functionTotals[bf] = addTotals(functionTotals[bf] ?? emptyTotals(), { count: 1, cost_usd: c.cost_usd });
    grandTotal = addTotals(grandTotal, { count: 1, cost_usd: c.cost_usd });
    if (c.flagged_for_review) {
      bumpCell(flaggedCells, `flagged|${bf}`, c);
      flaggedTotal = addTotals(flaggedTotal, { count: 1, cost_usd: c.cost_usd });
    }
  }

  return {
    cells,
    treatmentTotals,
    functionTotals,
    grandTotal,
    flagged: { cells: flaggedCells, total: flaggedTotal },
  };
}

// ---- Report 3: Investment × Engineer ------------------------------------

export interface InvestmentByEngineerRollup {
  /** key: `${InvestmentType}|${engineer_id}` */
  cells: Record<string, CellTotals>;
  investmentTotals: Record<InvestmentType, CellTotals>;
  engineerTotals: Record<string, CellTotals>;
  grandTotal: CellTotals;
}

export function rollupInvestmentByEngineer(
  view: AccountingView,
): InvestmentByEngineerRollup {
  const cells: Record<string, CellTotals> = {};
  const investmentTotals: Record<InvestmentType, CellTotals> = {
    ktlo: emptyTotals(),
    enablement: emptyTotals(),
    growth: emptyTotals(),
  };
  const engineerTotals: Record<string, CellTotals> = {};
  let grandTotal: CellTotals = emptyTotals();

  for (const c of view.commits) {
    const inv = c.decision.investment_type;
    const eng = c.author_engineer_id;
    bumpCell(cells, `${inv}|${eng}`, c);
    investmentTotals[inv] = addTotals(investmentTotals[inv], { count: 1, cost_usd: c.cost_usd });
    engineerTotals[eng] = addTotals(engineerTotals[eng] ?? emptyTotals(), { count: 1, cost_usd: c.cost_usd });
    grandTotal = addTotals(grandTotal, { count: 1, cost_usd: c.cost_usd });
  }

  return { cells, investmentTotals, engineerTotals, grandTotal };
}

// ---- Report 4: One engineer × BusinessFunction --------------------------

export function rollupInvestmentByFunctionForEngineer(
  view: AccountingView,
  engineerId: string,
): InvestmentByFunctionRollup {
  return rollupInvestmentByFunction({
    ...view,
    commits: view.commits.filter((c) => c.author_engineer_id === engineerId),
  });
}

// ---- Treatment × Engineer (per-engineer treatment table) ----------------

export interface TreatmentByEngineerRollup {
  /** key: `${TreatmentRow}|${engineer_id}` */
  cells: Record<string, CellTotals>;
  treatmentTotals: Record<TreatmentRow, CellTotals>;
  engineerTotals: Record<string, CellTotals>;
  grandTotal: CellTotals;
}

export function rollupTreatmentByEngineer(
  view: AccountingView,
): TreatmentByEngineerRollup {
  const cells: Record<string, CellTotals> = {};
  const treatmentTotals: Record<TreatmentRow, CellTotals> = {
    opex_rd: emptyTotals(),
    opex_other: emptyTotals(),
    capex: emptyTotals(),
  };
  const engineerTotals: Record<string, CellTotals> = {};
  let grandTotal: CellTotals = emptyTotals();

  for (const c of view.commits) {
    const row = treatmentRowFor(c.decision.accounting_treatment, c.decision.opex_subtype);
    const eng = c.author_engineer_id;
    bumpCell(cells, `${row}|${eng}`, c);
    treatmentTotals[row] = addTotals(treatmentTotals[row], { count: 1, cost_usd: c.cost_usd });
    engineerTotals[eng] = addTotals(engineerTotals[eng] ?? emptyTotals(), { count: 1, cost_usd: c.cost_usd });
    grandTotal = addTotals(grandTotal, { count: 1, cost_usd: c.cost_usd });
  }

  return { cells, treatmentTotals, engineerTotals, grandTotal };
}

// The Sankey's "by team" left axis groups commits by their author's
// user-defined team (see buildTeamGrouping / buildSankeyGraph). The
// per-engineer tables below stay individual; only the diagram groups.

// ---- Currency + percentage formatters -----------------------------------

export function fmtUSD(n: number, opts: { compact?: boolean } = {}): string {
  if (!Number.isFinite(n) || n === 0) return '$0';
  const compact = opts.compact ?? false;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  }).format(n);
}

export function fmtPct(numerator: number, denominator: number): string {
  if (denominator === 0) return '—';
  const pct = (numerator / denominator) * 100;
  if (pct === 0) return '0%';
  return `${pct.toFixed(pct < 10 ? 1 : 0)}%`;
}

// ---- Investment type display names --------------------------------------

export const INVESTMENT_LABELS: Record<InvestmentType, string> = {
  ktlo: 'KTLO',
  enablement: 'Enablement',
  growth: 'Growth',
};

export const TREATMENT_ROW_LABELS: Record<TreatmentRow, string> = {
  opex_rd: 'OpEx — R&D',
  opex_other: 'OpEx — Other (COGS / S&M / G&A)',
  capex: 'CapEx',
};

/** Short treatment labels for the Sankey nodes + per-engineer table. */
export const TREATMENT_NODE_LABELS: Record<TreatmentRow, string> = {
  opex_rd: 'OpEx — R&D',
  opex_other: 'OpEx',
  capex: 'CapEx',
};

export const OPEX_SUBTYPE_LABELS: Record<OpExSubtype, string> = {
  rd: 'R&D',
  cogs: 'COGS',
  sm: 'S&M',
  ga: 'G&A',
};
